import { lazy } from 'react';
import { Navigate, Route } from 'react-router-dom';

import { paths } from '@/shared/routes/paths';
import type { RouteHandle, TabSegment } from '@/shared/routes/route-handle';

import ParamRedirect from './param-redirect';

const Dashboard = lazy(() => import('@/features/dashboard/dashboard'));
const AccountSettings = lazy(() => import('@/features/account/account-shell'));
const MyLicence = lazy(() => import('@/features/licence/my-licence'));
const Lists = lazy(() => import('@/features/list/discovered-lps'));
const ListDetails = lazy(() => import('@/features/list/discovered-lp-detail'));
const ProfileExport = lazy(() => import('@/features/profile/profile-export'));
const Schools = lazy(() => import('@/features/school/my-schools'));
const Profiles = lazy(() => import('@/features/profile/my-profiles'));
const ProfileDetails = lazy(() => import('@/features/profile/profile-detail'));
const Marketplace = lazy(() => import('@/features/marketplace'));
const Imports = lazy(() => import('@/features/list/imports'));
const FileImport = lazy(() => import('@/features/list/file-import'));
const TrackedLPs = lazy(() => import('@/features/profile/tracked-lps'));
const Fundraisings = lazy(() => import('@/features/fundraising/fundraisings'));
const Faq = lazy(() => import('@/features/faq/client-faq'));

/** The root of every client trail (#693), kept out of the browser tab. */
const DASHBOARD: TabSegment = { label: 'Dashboard', to: paths.home, crumbOnly: true };

/**
 * Client zone: routes available to any authenticated user. Rendered as bare
 * <Route> elements so they can be inlined directly under the AppLayout route.
 *
 * `handle` carries each page's title (see RouteHandle). Every page follows the
 * admin layout (#658): `titleInPage` puts the h1 in the page (`<PageTitle />`)
 * and leaves the TopBar the trail. Every trail starts at the Dashboard, the
 * client's home (#693): a top-level page reads "Dashboard > Tracked LPs", a
 * child page adds its parent, "Dashboard > HNWIs > Profile Details". Each
 * parent is a link: the trail is the way back. The client zone has no section
 * word like "Admin": the side nav's headings ("Searches", "LP Management") are
 * menu labels, not places.
 * Detail pages declare the generic title here and push the entity name at
 * runtime via `useSetPageTitle`.
 *
 * Redirect-only routes carry no handle; they never render a page.
 */
export const clientRoutes = (
  <>
    <Route
      path={paths.home}
      element={<Dashboard />}
      handle={{ title: 'Dashboard', titleInPage: true } satisfies RouteHandle}
    />
    <Route
      path={paths.account}
      element={<AccountSettings />}
      handle={
        { title: 'Account', tabSegments: [DASHBOARD], titleInPage: true } satisfies RouteHandle
      }
    />
    {/* Legacy alias: the account page used to live at /profile. */}
    <Route path={paths.legacy.profile} element={<Navigate to={paths.account} replace />} />
    <Route
      path={paths.licence}
      element={<MyLicence />}
      handle={
        { title: 'Licence', tabSegments: [DASHBOARD], titleInPage: true } satisfies RouteHandle
      }
    />
    <Route path={paths.legacy.subscription} element={<Navigate to={paths.licence} replace />} />
    <Route
      path={paths.lists.root}
      element={<Lists />}
      handle={
        {
          title: 'Discovered LPs',
          tabSegments: [DASHBOARD],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.lists.detail(':id')}
      element={<ListDetails />}
      handle={
        {
          title: 'List Details',
          tabSegments: [{ label: 'Discovered LPs', to: paths.lists.root }, DASHBOARD],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.lists.export(':id')}
      element={<ProfileExport />}
      handle={
        {
          title: 'Export Profiles',
          // The list's own page would be the closer parent, but a handle is
          // static and cannot name a list id; the page has its own way back.
          tabSegments: [{ label: 'Discovered LPs', to: paths.lists.root }, DASHBOARD],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.trackedLps}
      element={<TrackedLPs />}
      handle={
        { title: 'Tracked LPs', tabSegments: [DASHBOARD], titleInPage: true } satisfies RouteHandle
      }
    />
    <Route path={paths.legacy.settings} element={<Navigate to={paths.account} replace />} />
    {/* Institutionals is hidden for the MVP (#658), for everyone; admins
        included. The paths stay declared so old links and bookmarks land on the
        dashboard rather than the catch-all. The pages are kept: to bring them
        back, restore the lazy imports of `@/features/company/institutionals` and
        `@/features/company/institutional-detail`, render them here (title
        'Institutionals', and 'Institutional Details' under a linked
        'Institutionals' crumb, both `titleInPage`), point the legacy /companies
        aliases at them again, and restore the side nav entry and the company
        links (profile page, profile drawer). */}
    <Route path={paths.institutionals.root} element={<Navigate to={paths.home} replace />} />
    <Route
      path={paths.institutionals.detail(':id')}
      element={<Navigate to={paths.home} replace />}
    />
    <Route path={paths.legacy.companies} element={<Navigate to={paths.home} replace />} />
    <Route
      path={paths.legacy.companyDetail(':id')}
      element={<Navigate to={paths.home} replace />}
    />
    <Route
      path={paths.schools}
      element={<Schools />}
      handle={
        { title: 'Schools', tabSegments: [DASHBOARD], titleInPage: true } satisfies RouteHandle
      }
    />
    <Route
      path={paths.profiles.all}
      element={<Profiles />}
      // Top-level: the trail is the page's own name. The page carries its h1
      // above the category band, the same header the admin indexes use.
      handle={{ title: 'HNWIs', tabSegments: [DASHBOARD], titleInPage: true } satisfies RouteHandle}
    />
    <Route
      path={paths.profiles.export}
      element={<ProfileExport />}
      handle={
        {
          title: 'Export Profiles',
          tabSegments: [{ label: 'HNWIs', to: paths.profiles.all }, DASHBOARD],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.profiles.detail(':id')}
      element={<ProfileDetails />}
      handle={
        {
          title: 'Profile Details',
          // The one way back: the page draws no trail and no back arrow of its own.
          tabSegments: [{ label: 'HNWIs', to: paths.profiles.all }, DASHBOARD],
          // The identity band carries the h1, so the trail keeps the name at crumb weight.
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.marketplace}
      element={<Marketplace />}
      handle={
        { title: 'Marketplace', tabSegments: [DASHBOARD], titleInPage: true } satisfies RouteHandle
      }
    />
    <Route
      path={paths.imports.root}
      element={<Imports />}
      handle={
        { title: 'Imports', tabSegments: [DASHBOARD], titleInPage: true } satisfies RouteHandle
      }
    />
    <Route
      path={paths.imports.new}
      element={<FileImport />}
      handle={
        {
          title: 'New Import',
          tabSegments: [{ label: 'Imports', to: paths.imports.root }, DASHBOARD],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.fundraisings.root}
      element={<Fundraisings />}
      handle={
        {
          title: 'Fundraising rounds',
          tabSegments: [DASHBOARD],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.faq}
      element={<Faq />}
      handle={{ title: 'FAQ', tabSegments: [DASHBOARD], titleInPage: true } satisfies RouteHandle}
    />
    <Route
      path={paths.legacy.fundraisingNew}
      element={<Navigate to={paths.fundraisings.dialog('new')} replace />}
    />
    <Route
      path={paths.legacy.fundraisingEdit(':id')}
      element={<ParamRedirect to={paths.fundraisings.dialog} />}
    />
  </>
);
